/**
 * As abas do portfólio, administradas pela tela.
 *
 * Até 25/10 eram doze nomes escritos em `frontend/src/areas.js`. Abrir a aba de
 * um setor novo era tarefa de engenharia: mexer no código, subir versão. Para
 * um dado que é de administração — quem decide que existe "Suprimentos" é quem
 * administra o portfólio, não quem programa.
 *
 * ## Três verbos, e eles não são equivalentes
 *
 * **Criar** é inócuo: aba vazia não muda nada para ninguém, e é assim que se
 * começa — a aba antes do primeiro item dela.
 *
 * **Desligar** tira da barra e **preserva os vínculos**. Religada, os itens
 * voltam exatamente como estavam. É o que se quer em quase todo caso em que se
 * pensa "apagar": o setor foi incorporado, a aba não serve mais, e ninguém
 * quer perder o que estava classificado nela.
 *
 * **Apagar** destrói os vínculos. Um item que só tinha aquela aba **some do
 * portfólio** — a tela de edição do item já diz isso ("sem nenhuma, ele some de
 * todas"), e aqui a consequência é a mesma vista do outro lado. Por isso apagar
 * aba com itens dentro exige confirmação, e a contagem vem do servidor: quem
 * clica lê "5 itens perdem esta aba" antes de decidir, não depois de procurar
 * os cinco.
 *
 * ## A ordem é das setas, e não do alfabeto
 *
 * Ordem alfabética seria grátis e estaria errada: a barra começa por Comercial
 * porque é onde a maioria procura, não porque "C" vem antes de "D". Quem
 * administra reordena; a lista não opina.
 *
 * ## Por que esquecer o cache depois de cada escrita
 *
 * `areas.js` guarda a lista por aba do navegador — ela quase nunca muda, e
 * buscá-la a cada navegação seria uma requisição por tela. Mas aqui ela
 * acabou de mudar, e sem `esquecerAreas()` o catálogo continuaria desenhando a
 * barra antiga até alguém recarregar a página: a edição pareceria não ter
 * funcionado, o que é pior do que não ter funcionado.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';
import { esquecerAreas } from '../areas.js';

const itens = (n) => (n === 1 ? '1 item' : `${n} itens`);

export function AdminAbas() {
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [nova, setNova] = useState('');
  const [criando, setCriando] = useState(false);
  const [renomeando, setRenomeando] = useState(null); // nome da aba em edição
  const [nomeNovo, setNomeNovo] = useState('');
  const [apagando, setApagando] = useState(null); // { nome, message, itens }
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await apiFetch('/api/admin/areas');
      setLista(r.areas ?? []);
      setErro(null);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Toda escrita passa por aqui.
   *
   * As rotas devolvem a lista inteira depois de mudar, então não há releitura:
   * a resposta É o estado novo. E `esquecerAreas()` fica num lugar só, porque
   * esquecer em cinco lugares é esquecer em quatro.
   */
  const escrever = useCallback(async (caminho, init, aoDarCerto) => {
    setOcupado(true);
    try {
      const r = await apiFetch(caminho, init);
      if (Array.isArray(r?.areas)) setLista(r.areas);
      esquecerAreas();
      aoDarCerto?.(r);
      return r;
    } catch (err) {
      if (err instanceof SessionError) {
        goToSignIn();
        return null;
      }
      throw err;
    } finally {
      setOcupado(false);
    }
  }, []);

  async function criar(e) {
    e.preventDefault();
    const nome = nova.trim();
    if (!nome || criando) return;

    setCriando(true);
    try {
      await escrever('/api/admin/areas', { method: 'POST', body: JSON.stringify({ nome }) }, () => {
        setNova('');
        notify(`A aba ${nome} entrou no fim da barra.`);
      });
    } catch (err) {
      notify(err.message, 'erro');
    } finally {
      setCriando(false);
    }
  }

  async function renomear(aba) {
    const nome = nomeNovo.trim();
    setRenomeando(null);
    if (!nome || nome === aba.nome) return;

    try {
      await escrever(
        `/api/admin/areas/${encodeURIComponent(aba.nome)}`,
        { method: 'PATCH', body: JSON.stringify({ nome }) },
        () =>
          notify(
            aba.itens > 0
              ? `${aba.nome} virou ${nome}, com ${itens(aba.itens)} dentro.`
              : `${aba.nome} virou ${nome}.`,
          ),
      );
    } catch (err) {
      notify(err.message, 'erro');
    }
  }

  async function alternar(aba) {
    try {
      await escrever(
        `/api/admin/areas/${encodeURIComponent(aba.nome)}`,
        { method: 'PATCH', body: JSON.stringify({ ativa: !aba.ativa }) },
        () =>
          notify(
            aba.ativa
              ? aba.itens === 0
                ? `${aba.nome} saiu da barra.`
                : `${aba.nome} saiu da barra. ${itens(aba.itens)} continuam classificados nela.`
              : `${aba.nome} voltou para a barra.`,
          ),
      );
    } catch (err) {
      notify(err.message, 'erro');
    }
  }

  /**
   * Move uma aba uma posição.
   *
   * Manda a lista inteira na ordem nova em vez de "sobe esta": a ordem é uma
   * sequência, e descrever o movimento deixaria servidor e tela discordando
   * sobre qual era o vizinho se alguém tivesse mexido no meio.
   */
  async function mover(aba, direcao) {
    const nomes = lista.map((a) => a.nome);
    const de = nomes.indexOf(aba.nome);
    const para = de + direcao;
    if (de < 0 || para < 0 || para >= nomes.length) return;

    nomes.splice(para, 0, ...nomes.splice(de, 1));

    try {
      await escrever('/api/admin/areas/ordem', { method: 'POST', body: JSON.stringify({ nomes }) });
    } catch (err) {
      notify(err.message, 'erro');
    }
  }

  /**
   * Apagar, em duas batidas.
   *
   * A primeira tenta sem confirmar. Se a aba tem itens, o servidor recusa com
   * 409 e manda a contagem — e é essa frase, de quem vai executar, que a tela
   * mostra. A tela não antecipa a recusa a partir da contagem que ela já tem:
   * a cópia no navegador pode estar velha, e a decisão precisa do número de
   * agora.
   */
  async function apagar(aba, confirmado = false) {
    try {
      const sufixo = confirmado ? '?confirmar=true' : '';
      const r = await escrever(
        `/api/admin/areas/${encodeURIComponent(aba.nome)}${sufixo}`,
        { method: 'DELETE' },
        () => setApagando(null),
      );
      if (r) {
        notify(
          r.itensAfetados > 0
            ? `${aba.nome} apagada. ${itens(r.itensAfetados)} ${r.itensAfetados === 1 ? 'perdeu' : 'perderam'} esta aba.`
            : `${aba.nome} apagada.`,
        );
      }
    } catch (err) {
      if (err.status === 409 && err.detail?.motivo === 'precisa_confirmar') {
        setApagando({ nome: aba.nome, message: err.message, itens: err.detail.itens });
        return;
      }
      notify(err.message, 'erro');
    }
  }

  const abas = lista ?? [];
  const desligadas = abas.filter((a) => !a.ativa).length;

  return (
    <div className="list-panel">
      {/* ⚠ `.list-panel` não tem preenchimento nenhum: é um contêiner rente,
          desenhado para a tabela encostar nas bordas. Texto solto dentro dele
          cola na lateral e no topo — foi o que aconteceu aqui, e por isso o
          cabeçalho tem caixa própria em vez de confiar no pai. */}
      <header className="abas-topo">
        <div className="abas-intro">
          <h3>Abas do portfólio</h3>
          <p>
            As abas em que o portfólio se divide, na ordem em que aparecem na barra. Cada item
            escolhe as suas na tela de edição dele, no Catálogo.
          </p>
          <p>
            <strong>Desligar</strong> tira a aba da barra e guarda os vínculos.{' '}
            <strong>Apagar</strong> desfaz os vínculos — e item que ficar sem nenhuma aba some do
            portfólio.
          </p>
        </div>

        <form className="abas-nova" onSubmit={criar}>
          <label className="abas-nova-campo">
            <span>Criar uma aba</span>
            <input
              className="input"
              value={nova}
              maxLength={80}
              placeholder="Ex.: Suprimentos"
              onChange={(e) => setNova(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="button primary"
            disabled={criando || nova.trim().length < 2}
          >
            {criando ? 'Criando…' : 'Criar aba'}
          </button>
        </form>
      </header>

      {carregando && <p className="state-message abas-rodape">Carregando…</p>}

      {erro && (
        <div className="state-message error abas-rodape" role="alert">
          <p>{erro}</p>
          <button className="button secondary" type="button" onClick={carregar}>
            Tentar de novo
          </button>
        </div>
      )}

      {!carregando && !erro && abas.length === 0 && (
        <p className="state-message abas-rodape">
          Nenhuma aba cadastrada. Sem nenhuma, o portfólio mostra só o filtro “Todos”.
        </p>
      )}

      {!carregando && !erro && abas.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Aba</th>
                <th className="num">Itens</th>
                <th>Na barra</th>
                <th className="col-action">Ordem</th>
                <th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {abas.map((aba, i) => (
                <tr key={aba.nome} className={aba.ativa ? '' : 'is-desligada'}>
                  <td>
                    {renomeando === aba.nome ? (
                      <input
                        className="input grupo-nome-campo"
                        autoFocus
                        value={nomeNovo}
                        maxLength={80}
                        aria-label="Nome da aba"
                        onChange={(e) => setNomeNovo(e.target.value)}
                        /* Sai salvando, como na aba de grupos: perder o que se
                           digitou por clicar fora é o jeito mais rápido de
                           alguém desistir de arrumar a barra. */
                        onBlur={() => renomear(aba)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renomear(aba);
                          if (e.key === 'Escape') setRenomeando(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="grupo-nome"
                        title="Clique para renomear"
                        onClick={() => {
                          setNomeNovo(aba.nome);
                          setRenomeando(aba.nome);
                        }}
                      >
                        {aba.nome}
                      </button>
                    )}
                  </td>

                  <td className="num">
                    {aba.itens === 0 ? (
                      <span className="cell-sub">vazia</span>
                    ) : (
                      <span title={`${itens(aba.itens)} aparecem nesta aba`}>{aba.itens}</span>
                    )}
                  </td>

                  <td>
                    <button
                      type="button"
                      className={`admin-toggle ${aba.ativa ? 'is-on' : ''}`}
                      disabled={ocupado}
                      aria-pressed={aba.ativa}
                      title={
                        aba.ativa
                          ? 'Clique para tirar da barra. Os vínculos continuam gravados.'
                          : 'Clique para voltar à barra. Os itens voltam como estavam.'
                      }
                      onClick={() => alternar(aba)}
                    >
                      {aba.ativa ? 'Aparece' : 'Desligada'}
                    </button>
                  </td>

                  <td className="col-action abas-ordem">
                    <button
                      type="button"
                      className="button ghost"
                      disabled={i === 0 || ocupado}
                      aria-label={`Mover ${aba.nome} para antes de ${abas[i - 1]?.nome ?? ''}`}
                      title="Subir"
                      onClick={() => mover(aba, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="button ghost"
                      disabled={i === abas.length - 1 || ocupado}
                      aria-label={`Mover ${aba.nome} para depois de ${abas[i + 1]?.nome ?? ''}`}
                      title="Descer"
                      onClick={() => mover(aba, 1)}
                    >
                      ↓
                    </button>
                  </td>

                  <td className="col-action">
                    {apagando?.nome === aba.nome ? (
                      /* A confirmação mora na própria linha, e não num balão:
                         o que se perde é desta aba, e ler a frase longe da
                         linha que a provocou é como se perde o referente. */
                      <div className="abas-confirma" role="alert">
                        <p>{apagando.message}</p>
                        <div className="abas-confirma-acoes">
                          <button
                            type="button"
                            className="button secondary"
                            onClick={() => setApagando(null)}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="button ghost abas-apagar"
                            disabled={ocupado}
                            onClick={() => apagar(aba, true)}
                          >
                            Apagar mesmo assim
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="button ghost abas-apagar"
                        disabled={ocupado}
                        title={
                          aba.itens > 0
                            ? `${itens(aba.itens)} perdem esta aba. Para só tirá-la da barra, desligue.`
                            : 'Aba vazia: apagar não alcança item nenhum'
                        }
                        onClick={() => apagar(aba)}
                      >
                        Apagar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {desligadas > 0 && (
        <p className="field-hint abas-rodape">
          {desligadas === 1
            ? '1 aba está desligada: não aparece na barra, e os itens dela continuam classificados.'
            : `${desligadas} abas estão desligadas: não aparecem na barra, e os itens delas continuam classificados.`}
        </p>
      )}
    </div>
  );
}

export default AdminAbas;
